import { z } from 'zod';
import {
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  MoneySchema,
  pageOf,
  withMeta,
} from '../common';
import { TierSchema } from './me';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** billing family — `manage.billing.*`: Billing & Plans (SDD §7.4): plan cards with entitlement diff, checkout hand-off,
 *  portal, invoices. Stripe webhooks (signature-verified, idempotent) are the only state source; nothing is granted locally. */

/** How a plan is paid for. Yearly bills the same package at 20% less per month (owner, 2026-08-22). */
export const BillingIntervalSchema = z.enum(['monthly', 'yearly']);
export type BillingInterval = z.infer<typeof BillingIntervalSchema>;

export const PlanSchema = z
  .object({
    id: IdSchema,
    tier: TierSchema,
    name: z.string(),
    priceMonthly: MoneySchema,
    /** What a month costs when the year is paid up front — 20% below `priceMonthly`. */
    priceMonthlyYearly: MoneySchema,
    currency: z.string().length(3),
    /** The one line under the price: who the plan is for. */
    blurb: z.string(),
    /** The plan's own selling points, as written. `inherits` carries the "Everything in X" line, so these are the
     *  increments over the tier below rather than a restatement of it. */
    highlights: z.array(z.string()),
    /** Name of the tier this one builds on, or null for the first. */
    inherits: z.string().nullable(),
    /** Set on the tier the catalogue marks RECOMMENDED. */
    recommended: z.boolean(),
    /** Entitlement feature keys — what the kernel cascade grants. Distinct from `highlights`, which is copy. */
    features: z.array(z.string()),
    limits: z
      .object({
        accountSlots: z.number().int(),
        aiDailyBudget: z.number().int(),
        voiceMinutes: z.number().int(),
      })
      .strict(),
    current: z.boolean(),
  })
  .strict();
export const SubscriptionSchema = z
  .object({
    planId: IdSchema,
    tier: TierSchema,
    status: z.enum(['active', 'trialing', 'past_due', 'canceled', 'pending_webhook']),
    interval: BillingIntervalSchema,
    renewsAt: IsoDateTimeSchema.nullable(),
    discountCode: z.string().nullable(),
  })
  .strict();
export const PlansSchema = withMeta({
  plans: z.array(PlanSchema),
  subscription: SubscriptionSchema,
  /** Entitlement diff of each plan vs the current one (feature keys gained / lost). */
  diff: z.record(z.string(), z.object({ gained: z.array(z.string()), lost: z.array(z.string()) }).strict()),
});
export const InvoiceSchema = z
  .object({
    id: IdSchema,
    number: z.string(),
    amount: MoneySchema,
    currency: z.string().length(3),
    status: z.enum(['paid', 'open', 'void', 'uncollectible']),
    issuedAt: IsoDateTimeSchema,
    pdfUrl: z.string().nullable(),
  })
  .strict();
export const CheckoutSchema = withMeta({
  // The PROVIDER'S id, not ours: a Stripe Checkout Session id (`cs_…`) runs past our 26-char ULID shape and past
  // 64 characters — the first live-branch test refused a session the provider had genuinely opened. An opaque
  // bounded string is the honest type for an identifier another system mints.
  checkoutId: z.string().min(1).max(128),
  /** Hand-off URL to the payment provider's hosted checkout; the app never collects card data. */
  url: z.string(),
  state: z.enum(['created', 'pending_webhook', 'completed', 'expired']),
  /** Discount codes carry reservation semantics: reserved at checkout, consumed on webhook. */
  discountReserved: z.boolean(),
});

export const billingProcedures = defineProcedures({
  'manage.billing.plans': query('Plans + current subscription + entitlement diff', z.void(), PlansSchema),
  'manage.billing.checkout': mutation(
    'Create a hosted checkout session for a plan (with optional discount code reservation)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        planId: IdSchema,
        interval: BillingIntervalSchema.default('monthly'),
        discountCode: z.string().max(32).nullable().optional(),
      })
      .strict(),
    CheckoutSchema,
  ),
  'manage.billing.portal': mutation(
    'Open the billing portal (hand-off URL)',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    withMeta({ url: z.string() }),
  ),
  'manage.billing.invoices': query('Invoices (cursor)', CursorInputSchema.optional(), pageOf(InvoiceSchema)),
  'manage.billing.subscription': query(
    'Current subscription',
    z.void(),
    withMeta({ subscription: SubscriptionSchema }),
  ),
});
export type Plan = z.infer<typeof PlanSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type Plans = z.infer<typeof PlansSchema>;
export type Invoice = z.infer<typeof InvoiceSchema>;
export type Checkout = z.infer<typeof CheckoutSchema>;
export type BillingFamilyClient = RouterShape<typeof billingProcedures>;

export const billingFamily = defineFamily({
  name: 'billing',
  title: 'Billing & Plans',
  screens: ['Billing & Plans'],
  procedures: billingProcedures,
  binding: 'simulated',
});
